import { Injectable } from '@angular/core';
import { ShellClientService } from 'projects/shared-components/shell-communication/shell-client.service';
import {
    GetLandmineDaysShell,
    GetLandmineDaysShellReply,
    LandmineDayWireDto,
    LandmineEventWireDto
} from 'projects/shared-components/shell-communication/shell-operations-protocol';
import {
    createLandmineDayMap,
    enumerateDayKeys,
    LandmineCoverageStatus,
    LandmineDay,
    LandmineDayMap,
    LandmineEvent,
    LandmineEventKind,
    LandmineSession,
    LandmineTier,
    LANDMINE_EVENT_KINDS,
    LANDMINE_SESSIONS,
    LANDMINE_TIERS,
    unknownLandmineDay
} from './landmine-day.model';

/**
 * THE single fetch point for the Events Calendar (CR-20260904-001, contract C1 section 4).
 *
 * Both consumers - ORION's roll-positions filtering and the Price Box colouring - read the map
 * this service returns. Neither of them calls the Shell for landmines itself, and neither derives
 * a tier: the verdict is already resolved by the backend.
 *
 * What it guarantees:
 *   - A row for EVERY date in the requested span. A date the backend did not answer comes back
 *     "unknown" - never "clear" (C1 G1).
 *   - The same span asked twice inside the cache lifetime returns the SAME map instance, so a run
 *     and a repaint cannot disagree (G7).
 *   - A FAILURE REJECTS (owner ruling 2026-09-08: the system must fail loudly). A transport
 *     failure, a timeout, an unauthorised operation, a Shell fault (the calendar's facts service
 *     down), a malformed reply or an impossible span all reject with the cause, and the cause is
 *     logged. The consumer decides how to surface it; nothing here turns an outage into a quiet
 *     map of "unknown" days.
 *   - A DATA GAP is not a failure. A Shell answer of status "unavailable" means the store
 *     answered and covers none of the span; it resolves to a map whose every row is "unknown".
 *     "partial" likewise resolves, with the uncovered days "unknown" (C1 G1).
 *   - A failure cannot be hammered. A rejection (and a data-gap "unavailable") is held for a
 *     short RETRY FLOOR (30 seconds) rather than dropped outright, so a caller repainting in a
 *     loop cannot turn an outage into a request storm - every ask inside the floor gets the SAME
 *     rejection, loudly, without a request. Every suppressed ask says so in the log.
 *     See UNAVAILABLE_RETRY_FLOOR_MS.
 *   - It rejects, loudly, any record whose tier or event kind is outside the closed vocabulary.
 *     A row it cannot trust becomes "unknown"; the answer degrades to "partial".
 */
@Injectable()
export class LandmineDaysService {

    /**
     * Cache lifetime: 15 MINUTES (assignment 01). Bounded, and well inside one trading session -
     * a column rebuild after that re-asks the Shell. An identical span inside the window is served
     * from cache and therefore returns the same instance.
     */
    static readonly CACHE_LIFETIME_MS = 15 * 60 * 1000;

    /**
     * Retry floor after an "unavailable" answer: 30 SECONDS (assignment 03, leader's ruling).
     *
     * Why it exists. An "unavailable" answer used to be dropped from the cache the instant it
     * resolved, so the NEXT ask - even one line later - issued a fresh request. The APG host asks
     * at every Price Box column build, so during an outage the retry rate was simply the caller's
     * call rate, bounded only by round-trip latency. The failure that actually reaches production
     * is a mis-granted client: the Shell rejects that on entitlement, which is CHEAP for it to
     * produce, so that case returns fastest and therefore loops fastest.
     *
     * What it is NOT: not a backoff, not a circuit breaker. The floor is flat and short, so a real
     * outage is still visible within half a minute. This buys a floor; it does not hide a failure.
     */
    static readonly UNAVAILABLE_RETRY_FLOOR_MS = 30 * 1000;

    private readonly _cache: {
        [span: string]: {
            stampedAt: number,      // when this entry became the answer we serve
            lifetimeMs: number,     // how long it may be served for
            unavailable: boolean,   // true once it resolved "unavailable" - i.e. it is a floor
            promise: Promise<LandmineDayMap>
        }
    } = {};

    constructor(private readonly _shellClient: ShellClientService) {
    }

    /**
     * The verdicts for [from, to] inclusive, both "yyyy-MM-dd" labels.
     * REJECTS on failure (see the class note); resolves on a data gap.
     */
    getDays(from: string, to: string): Promise<LandmineDayMap> {
        const span = from + '|' + to;
        const cached = this._cache[span];

        if (cached) {
            const age = Date.now() - cached.stampedAt;
            if (age < cached.lifetimeMs) {
                // A suppressed retry and a caller that never asked look IDENTICAL in a log unless
                // the suppression says so out loud. One line per suppressed ask, on purpose: it is
                // the only evidence that the caller did ask and we deliberately did not go.
                if (cached.unavailable) {
                    console.warn(
                        '[landmines] retry suppressed for ' + span + ' - the calendar failed or ' +
                        'answered "unavailable" ' + age + 'ms ago and the ' +
                        LandmineDaysService.UNAVAILABLE_RETRY_FLOOR_MS +
                        'ms floor has not expired. No request issued; serving that same outcome.');
                }
                return cached.promise;
            }
        }

        const promise = this.load(from, to);
        const entry = {
            stampedAt: Date.now(),
            lifetimeMs: LandmineDaysService.CACHE_LIFETIME_MS,
            unavailable: false,
            promise: promise
        };
        this._cache[span] = entry;

        // A rejection, or a data-gap "unavailable", is never held for the full 15 minutes. It is
        // held for the retry floor instead - long enough that a repainting caller cannot hammer the
        // Shell, short enough that a real outage stays visible. In-flight callers share this
        // promise either way, so concurrent asks can never disagree. A held REJECTION rejects every
        // caller inside the floor: the floor limits requests, never loudness.
        const floor = () => {
            if (this._cache[span] === entry) {
                entry.unavailable = true;
                entry.lifetimeMs = LandmineDaysService.UNAVAILABLE_RETRY_FLOOR_MS;
                // Stamped from the moment the outcome became KNOWN, not from when it was asked:
                // a slow failure would otherwise spend most of its floor inside its own round trip.
                entry.stampedAt = Date.now();
            }
        };

        promise.then(
            map => { if (map.status === 'unavailable') { floor(); } },
            () => { floor(); }
        );

        return promise;
    }

    /** Drops every cached span. For a consumer that knows its data is stale (none today). */
    invalidate(): void {
        const spans = Object.keys(this._cache);
        for (let i = 0; i < spans.length; i++) {
            delete this._cache[spans[i]];
        }
    }

    // --- internals -----------------------------------------------------------------------------

    private async load(from: string, to: string): Promise<LandmineDayMap> {
        const requested = enumerateDayKeys(from, to);

        if (requested.length === 0) {
            const message = '[landmines] refusing to ask for an impossible span: ' + from + '..' + to;
            console.error(message);
            throw new Error(message);
        }

        let reply: GetLandmineDaysShellReply;
        try {
            // processQuery can throw SYNCHRONOUSLY (no session, no shell id) as well as reject;
            // inside an async method both land in this catch.
            reply = await this._shellClient
                .processQuery<GetLandmineDaysShellReply>(new GetLandmineDaysShell(from, to));
        } catch (error) {
            // The Shell faults when its facts service is down (owner ruling 2026-09-08); this is
            // that fault, or the transport's own. It is NOT turned into a map of unknown days.
            const cause = LandmineDaysService.describe(error);
            console.error(
                '[landmines] GetLandmineDaysShell FAILED for ' + from + '..' + to + ': ' + cause, error);
            throw new Error('Events Calendar failed for ' + from + '..' + to + ': ' + cause);
        }

        return this.toMap(from, to, requested, reply);
    }

    /** One line of cause for a toast or a log, whatever shape the transport handed back. */
    static describe(error: any): string {
        if (!error) {
            return 'unknown error';
        }
        if (typeof error === 'string') {
            return error;
        }
        const parts: string[] = [];
        if (error.name && error.name !== 'Error') {
            parts.push(String(error.name));
        }
        if (error.message) {
            parts.push(String(error.message));
        } else if (error.error && error.error.message) {
            parts.push(String(error.error.message));
        }
        return parts.length > 0 ? parts.join(': ') : String(error);
    }

    private toMap(
        from: string,
        to: string,
        requested: string[],
        reply: GetLandmineDaysShellReply
    ): LandmineDayMap {

        if (!reply || !Array.isArray(reply.days)) {
            console.error('[landmines] DEFECT: malformed reply for ' + from + '..' + to, reply);
            throw new Error('Events Calendar returned a malformed reply for ' + from + '..' + to);
        }

        if (reply.status === 'unavailable') {
            // A data gap, not a failure: the store answered and vouches for none of these days.
            console.warn(
                '[landmines] the store covers none of ' + from + '..' + to +
                ' (asOf ' + reply.asOf + ') - every day reads "unknown"');
            return this.unavailableMap(from, to, requested, reply.asOf);
        }

        const byKey: { [dateKey: string]: LandmineDay } = {};

        // Start from the SHELL's own verdict on its coverage. We may only make it worse, never
        // better: a service that says "partial" is not turned into "ok" by us finding every row.
        let degraded = reply.status !== 'ok';

        if (reply.status !== 'ok' && reply.status !== 'partial') {
            console.error(
                '[landmines] DEFECT: status "' + reply.status + '" for ' + from + '..' + to +
                ' is outside the closed vocabulary (ok / partial / unavailable). ' +
                'Treating the answer as partial.');
        }

        const wireDays = reply.days || [];
        for (let i = 0; i < wireDays.length; i++) {
            const row = this.toDay(wireDays[i]);
            if (!row) {
                degraded = true;
                continue;
            }
            if (row.tier === 'unknown') {
                degraded = true;
            }
            byKey[row.date] = row;
        }

        // Every date the caller asked about gets a row. A date the backend skipped is "unknown",
        // never "clear" - absence must never be readable as "safe to trade".
        const days: LandmineDay[] = [];
        for (let i = 0; i < requested.length; i++) {
            const key = requested[i];
            const row = byKey[key];
            if (row) {
                days.push(row);
            } else {
                days.push(unknownLandmineDay(key));
                degraded = true;
            }
        }

        const status: LandmineCoverageStatus = degraded ? 'partial' : 'ok';

        if (degraded) {
            console.warn(
                '[landmines] coverage is partial for ' + from + '..' + to +
                ' - some days read "unknown"');
        }

        // `coverage` is what the STORE vouches for (C1 section 2) - passed through, not replaced
        // by the question. It falls back to the question only when the reply carries none.
        const coverage = reply.coverage && reply.coverage.from && reply.coverage.to
            ? { from: reply.coverage.from, to: reply.coverage.to }
            : { from: from, to: to };

        return createLandmineDayMap(reply.asOf, coverage, status, days);
    }

    /**
     * One wire row -> one verdict record, or null when the row cannot be trusted.
     * A rejected row is replaced by an "unknown" day by the caller: we never fall back to "clear".
     */
    private toDay(wire: LandmineDayWireDto): LandmineDay | null {
        if (!wire || typeof wire.dayKey !== 'string' || wire.dayKey.length === 0) {
            console.error('[landmines] DEFECT: a verdict row has no day key', wire);
            return null;
        }

        if (LANDMINE_TIERS.indexOf(wire.tier) < 0) {
            console.error(
                '[landmines] DEFECT: tier "' + wire.tier + '" on ' + wire.dayKey +
                ' is outside the closed vocabulary (T1 / T2 / clear / unknown). ' +
                'Reading the day as "unknown".');
            return unknownLandmineDay(wire.dayKey);
        }

        const wireEvents = wire.events || [];
        const events: LandmineEvent[] = [];

        for (let i = 0; i < wireEvents.length; i++) {
            const event = this.toEvent(wireEvents[i], wire.dayKey);
            if (!event) {
                // A row whose events we cannot read is a row we cannot explain, and a row we
                // cannot explain is not a row we let ORION act on (C1 G4/G5).
                return unknownLandmineDay(wire.dayKey);
            }
            events.push(event);
        }

        return {
            date: wire.dayKey,
            tradingDay: !!wire.tradingDay,
            tier: wire.tier as LandmineTier,
            estimated: !!wire.estimated,
            events: events
        };
    }

    private toEvent(wire: LandmineEventWireDto, dayKey: string): LandmineEvent | null {
        if (!wire) {
            console.error('[landmines] DEFECT: an empty event on ' + dayKey);
            return null;
        }

        if (LANDMINE_EVENT_KINDS.indexOf(wire.kind) < 0) {
            console.error(
                '[landmines] DEFECT: event kind "' + wire.kind + '" on ' + dayKey +
                ' is outside the closed vocabulary (FOMC / CPI / NFP / Earnings).');
            return null;
        }

        // The contract asks for tier and kind to be validated. Session is explanatory only - the
        // date arithmetic is already in effectDate - so an unexpected session is reported but does
        // not cost the row its verdict.
        if (LANDMINE_SESSIONS.indexOf(wire.session) < 0) {
            console.error(
                '[landmines] unexpected session "' + wire.session + '" on ' + dayKey +
                ' (expected pre-market / after-close)');
        }

        return {
            kind: wire.kind as LandmineEventKind,
            ticker: wire.ticker ? wire.ticker : undefined,
            releaseDate: wire.releaseDayKey,
            effectDate: wire.effectDayKey,
            session: wire.session as LandmineSession,
            confirmed: !!wire.confirmed
        };
    }

    private unavailableMap(
        from: string,
        to: string,
        requested: string[],
        asOf: string = ''
    ): LandmineDayMap {
        const days: LandmineDay[] = [];
        for (let i = 0; i < requested.length; i++) {
            days.push(unknownLandmineDay(requested[i]));
        }

        return createLandmineDayMap(asOf || '', { from: from, to: to }, 'unavailable', days);
    }
}
